import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, IconButton, Popover, ToggleButton, ToggleButtonGroup, Tooltip, Typography, useTheme } from '@mui/material';
import DateRangeRoundedIcon from '@mui/icons-material/DateRangeRounded';
import { DatePicker } from '@mui/x-date-pickers';
import {
    getPerformancePickerTextFieldProps, performanceCompactLabelSx, performanceInsetSx,
    performancePickerPopperSx, performancePopoverPaperSx, performanceSecondaryControlSx,
    performanceToggleButtonSx, performanceToggleGroupSx,
} from '../styles/performanceMsiStyles';

const LOOKBACKS = ['1W', 'MTD', '1M', '3M', 'YTD', '1Y'];

const toggleBtnSx = {
    color: '#eee',
    px: 1.2,
    '&.Mui-selected': {
        bgcolor: '#11161c',
        color: '#fff',
        borderColor: '#777',
    },
    '&.Mui-selected:hover': { bgcolor: '#1a232d' },
    ...performanceToggleButtonSx,
};

// ---------- date helpers ----------
const toISO = (d) => d.toISOString().slice(0, 10);
const parseISO = (s) => new Date(`${s}T00:00:00Z`);
// DatePicker uses local calendar dates; UTC conversion can move the selected
// vendor date across a day boundary in either direction.
const parsePickerDate = (iso) => {
    const [year, month, day] = iso.split('-').map(Number);
    return new Date(year, month - 1, day);
};
const pickerToISO = (v) => (v instanceof Date && !isNaN(v)
    ? `${v.getFullYear()}-${String(v.getMonth() + 1).padStart(2, '0')}-${String(v.getDate()).padStart(2, '0')}`
    : null);

const shiftDays = (iso, delta) => {
    const d = parseISO(iso);
    d.setUTCDate(d.getUTCDate() + delta);
    return toISO(d);
};
const shiftMonths = (iso, delta) => {
    const d = parseISO(iso);
    d.setUTCMonth(d.getUTCMonth() + delta);
    return toISO(d);
};
const startOfMonth = (iso) => {
    const d = parseISO(iso);
    d.setUTCDate(1);
    return toISO(d);
};
const startOfYear = (iso) => {
    const d = parseISO(iso);
    d.setUTCMonth(0, 1);
    return toISO(d);
};
const isValidISO = (s) => !!s && /^\d{4}-\d{2}-\d{2}$/.test(s);

export default function PerformanceWindowControls({ lookback, onLookbackChange, latestDate, windowEndCap, requestedStartDate, requestedEndDate, currentAttribution = false, label = 'Lookback', compact = false, showRange = false, children }) {
    const theme = useTheme();
    const [customRangeAnchorEl, setCustomRangeAnchorEl] = useState(null);
    const [pendingStart, setPendingStart] = useState(null);
    const [pendingEnd, setPendingEnd] = useState(null);
    const [highlightEnd, setHighlightEnd] = useState(false);
    const maxAvailISO = windowEndCap || latestDate || null;
    const customRangePopoverOpen = Boolean(customRangeAnchorEl);
    const isCustomLookback = /^\d{4}-\d{2}-\d{2}\.\.\d{4}-\d{2}-\d{2}$/.test(lookback || '');
    // Keep the date inputs in sync with whichever lookback is active
    useEffect(() => {
        if (!latestDate && !maxAvailISO && !isCustomLookback) return;

        if (isCustomLookback) {
            const [s, e] = lookback.split('..');
            setPendingStart(isValidISO(s) ? s : null);
            setPendingEnd(isValidISO(e) ? e : null);
        } else if (currentAttribution && isValidISO(requestedStartDate) && isValidISO(requestedEndDate)) {
            setPendingStart(requestedStartDate);
            setPendingEnd(requestedEndDate);
        } else {
            const cap = maxAvailISO || latestDate;
            let s = null, e = cap;
            switch (lookback) {
                case '1W': s = shiftDays(e, -7); break;
                case '1M': s = shiftMonths(e, -1); break;
                case '3M': s = shiftMonths(e, -3); break;
                case '1Y': s = shiftMonths(e, -12); break;
                case 'MTD': s = startOfMonth(e); break;
                case 'YTD': s = startOfYear(e);  break;
                default:   s = shiftMonths(e, -12);
            }
            if (currentAttribution && !['MTD', 'YTD'].includes(lookback)) s = shiftDays(s, 1);
            setPendingStart(s);
            setPendingEnd(e);
        }
    }, [lookback, isCustomLookback, latestDate, maxAvailISO, currentAttribution, requestedStartDate, requestedEndDate]);

    const customRangeIsValid = isValidISO(pendingStart) && isValidISO(pendingEnd) && pendingStart <= pendingEnd && (!maxAvailISO || pendingEnd <= maxAvailISO);

    useEffect(() => {
        if (!pendingEnd) return;
        if (
            (pendingStart && pendingEnd < pendingStart) ||
            (maxAvailISO && pendingEnd > maxAvailISO)
        ) {
            setPendingEnd(null);
        }
    }, [pendingStart, pendingEnd, maxAvailISO]);

    const commitIfReadyNext = useCallback(
        (nextEndISO, closePopover = false) => {
            const endISO = nextEndISO ?? pendingEnd;
            if (!isValidISO(pendingStart) || !isValidISO(endISO) || pendingStart > endISO || (maxAvailISO && endISO > maxAvailISO)) return;
            const next = `${pendingStart}..${endISO}`;
            if (next !== lookback) onLookbackChange(next);
            if (closePopover) {
                setCustomRangeAnchorEl(null);
                setHighlightEnd(false);
            }
        },
        [pendingStart, pendingEnd, lookback, onLookbackChange, maxAvailISO]
    );

    return <>
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 0.5,
                pl: 1,
                pr: 1,
                borderRadius: 1,
                border: '1px solid #555',
                bgcolor: '#000',
                flexWrap: 'wrap',
                ...performanceInsetSx,
                ...(compact ? { p: 0, pl: 0, pr: 0, border: 0, bgcolor: 'transparent', flexWrap: showRange ? 'wrap' : 'nowrap', minWidth: 0, maxWidth: '100%' } : {}),
            }}
        >
            <Typography variant="caption" sx={{ ...performanceCompactLabelSx, mr: 0.5 }}>{label}:</Typography>
            <ToggleButtonGroup
                value={isCustomLookback ? null : lookback}
                exclusive
                onChange={(_, v) => v && onLookbackChange(v)}
                size="small"
                sx={{
                    height: 32,
                    border: '1px solid #555',
                    bgcolor: '#000',
                    ...performanceToggleGroupSx,
                    ...(compact ? { flexShrink: 0 } : {}),
                    '& .MuiToggleButtonGroup-grouped:not(:last-of-type)': { border: 'none' },
                    '& .MuiToggleButtonGroup-grouped:not(:first-of-type)': { border: 'none' },
                }}
            >
                {LOOKBACKS.map((lb) => (
                    <ToggleButton key={lb} value={lb} sx={toggleBtnSx}>
                        {lb}
                    </ToggleButton>
                ))}
            </ToggleButtonGroup>
            <Tooltip title="Advanced custom date range" placement="top" arrow>
                <IconButton
                    size="small"
                    onClick={(event) => setCustomRangeAnchorEl(event.currentTarget)}
                    sx={{
                        color: '#dbe7f3',
                        border: '1px solid #5d6a78',
                        borderRadius: 1,
                        p: 0.55,
                        '&:hover': { borderColor: '#8ca0b4', bgcolor: 'rgba(140,160,180,0.08)' },
                        ...performanceSecondaryControlSx,
                    }}
                >
                    <DateRangeRoundedIcon sx={{ fontSize: 18 }} />
                </IconButton>
            </Tooltip>
            {(showRange || (!compact && isCustomLookback)) && pendingStart && pendingEnd && (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {`${pendingStart} -> ${pendingEnd}`}
                </Typography>
            )}
            {children}
        </Box>
        <Popover
            open={customRangePopoverOpen}
            anchorEl={customRangeAnchorEl}
            onClose={() => {
                setCustomRangeAnchorEl(null);
                setHighlightEnd(false);
            }}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            slotProps={{
                paper: {
                    sx: {
                        mt: 0.6,
                        p: 1.2,
                        minWidth: 320,
                        bgcolor: '#000',
                        color: '#e5e7eb',
                        border: '1px solid rgba(148, 163, 184, 0.55)',
                        display: 'grid',
                        gap: 1,
                        ...performancePopoverPaperSx,
                    },
                },
            }}
        >
            <Typography variant="caption" sx={{ ...performanceCompactLabelSx, fontWeight: 700 }}>
                Custom Date Range
            </Typography>
            <Box sx={{ display: 'grid', gap: 0.4 }}>
                <Typography variant="caption" color="text.secondary">Start</Typography>
                <DatePicker
                    value={pendingStart ? parsePickerDate(pendingStart) : null}
                    maxDate={pendingEnd ? parsePickerDate(pendingEnd) : (maxAvailISO ? parsePickerDate(maxAvailISO) : undefined)}
                    onChange={(v) => {
                        const iso = pickerToISO(v);
                        setPendingStart(iso);
                        if (iso) {
                            setHighlightEnd(true);
                            setPendingEnd((prev) => (prev && prev < iso ? null : prev));
                        }
                    }}
                    slotProps={{
                        textField: getPerformancePickerTextFieldProps(theme),
                        popper: { sx: performancePickerPopperSx },
                    }}
                />
            </Box>
            <Box sx={{ display: 'grid', gap: 0.4 }}>
                <Typography variant="caption" color="text.secondary">End</Typography>
                <DatePicker
                    value={pendingEnd ? parsePickerDate(pendingEnd) : null}
                    minDate={pendingStart ? parsePickerDate(pendingStart) : undefined}
                    maxDate={maxAvailISO ? parsePickerDate(maxAvailISO) : undefined}
                    referenceDate={
                        pendingEnd
                            ? parsePickerDate(pendingEnd)
                            : pendingStart
                                ? parsePickerDate(pendingStart)
                                : maxAvailISO
                                    ? parsePickerDate(maxAvailISO)
                                    : undefined
                    }
                    onChange={(v) => {
                        const iso = pickerToISO(v);
                        setPendingEnd(iso);
                        setHighlightEnd(false);
                    }}
                    slotProps={{
                        textField: getPerformancePickerTextFieldProps(theme, { highlight: highlightEnd }),
                        popper: { sx: performancePickerPopperSx },
                    }}
                />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.8 }}>
                <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                        setCustomRangeAnchorEl(null);
                        setHighlightEnd(false);
                    }}
                    sx={performanceSecondaryControlSx}
                >
                    Close
                </Button>
                <Button
                    size="small"
                    variant="contained"
                    disabled={!customRangeIsValid}
                    onClick={() => commitIfReadyNext(undefined, true)}
                    sx={{ textTransform: 'none', fontWeight: 700 }}
                >
                    Apply
                </Button>
            </Box>
        </Popover>
    </>;
}
